import { push, replace } from "react-router-redux";
import { call, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { AVAILABLE_REGIONS, REGION_REST_OF_LATAM, REGION_USA } from "constants.js";
import { parseInt } from "lodash";
import * as session from "middleware/session";
import { actions as assistantActions, selectors as assistantSelectors } from "reducers/assistant";
import { types as enrollmentTypes } from "reducers/enrollment";
import { types as fingerprintTypes } from "reducers/fingerprint";
import { actions as i18nActions } from "reducers/i18n";
import { actions as loginActions, selectors, types } from "reducers/login";
import { actions as notificationActions } from "reducers/notification";
import { actions as sessionActions, selectors as sessionSelectors } from "reducers/session";
import { actions as statusActions } from "reducers/status";
import { actions as tokenActions, types as tokenTypes } from "reducers/token";
import formTypes from "reducers/types/form";
import { globalTypes } from "reducers/types/global";
import { delay } from "redux-saga";
import * as configUtils from "util/config";
import * as deviceUtils from "util/device";
import * as utilDownload from "util/download";
import * as fingerprintUtils from "util/fingerprint";
import { adjustIdFieldErrors } from "util/form";
import currentLocation from "util/geolocation";
import * as i18n from "util/i18n";
import { NOTOKENLOGIN } from "util/login";
import * as pushNotificationsUtils from "util/pushNotifications";
import * as secureStorageUtils from "util/secureStorage";
import { getPostLoginHomePath, getLoginStep4Path, getUnauthenticatedLoginPath } from "util/v3UxConfig";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { decryptCredential } from "util/credentialEncryption";

const isLoginV3PathSelector = (state) => (state?.router?.location?.pathname || "").startsWith("/v3/");

const hasRememberedCredentials = (rememberedUser) =>
    Boolean(rememberedUser) &&
    (Boolean(rememberedUser.username) ||
        (Boolean(rememberedUser.document) && Boolean(rememberedUser.documentType)));

const sagas = [
    takeEvery(types.RESET, reset),

    takeLatest(types.LOGIN_STEP_1_REQUEST, handleLoginStep1Request),
    takeLatest(types.LOGIN_STEP_2_REQUEST, handleLoginStep2Request),
    takeLatest(types.LOGIN_OAUTH_REQUEST, handleOauthRequest),
    takeLatest(types.LOGIN_STEP_3_REQUEST, handleLoginStep3Request),
    takeLatest(types.LOGIN_STEP_4_REQUEST, handleLoginStep4Request),
    takeLatest(types.FINALIZE_LOGIN, handleFinalizeLogin),

    takeLatest(types.FINGERPRINT_LOGIN_PRE, handleFingerprintLoginPre),
    takeLatest(types.GO_BACK_LOGIN_SHOW_MESSAGE, goBackToLoginAndShowMessage),
    takeLatest(types.GET_CLIENT_COUNTRY_REQ, getClientCountry),
    takeLatest(types.GET_RANDOM_SECURITY_SEAL, getRandomSecuritySeal),
    takeLatest(types.DOWNLOAD_TERMS_REQUEST, handleDownloadTerms),
    takeLatest(types.DOWNLOAD_TERMS_I18N_REQUEST, handleDownloadTermsI18n),
];

export default sagas;

function* getRandomSecuritySeal() {
    const { type, data } = yield call(session.getRandomSecuritySeal);
    if (type !== "W") {
        const { _securitySeal, _securitySealAlt } = data.data;
        yield put({
            type: types.SET_RANDOM_SECURITY_SEAL,
            securitySeal: _securitySeal,
            securitySealAlt: _securitySealAlt,
        });
    }
}

function* getClientCountry() {
    const activeRegion = yield select(selectors.getRegion);
    if (!activeRegion) {
        try {
            const { type, data } = yield call(session.getClientCountry);
            if (type !== "W") {
                const { country } = data.data;
                if (country) {
                    yield put({ type: types.GET_CLIENT_COUNTRY_RES, clientCountry: country.iso_code });

                    // Verify if the client country is in our supported countries
                    if (AVAILABLE_REGIONS.indexOf(country.iso_code) !== -1) {
                        yield put({ type: types.SET_REGION, region: country.iso_code });
                    } else {
                        yield put({ type: types.SET_REGION, region: REGION_REST_OF_LATAM });
                    }
                }
            } else {
                yield put({ type: types.SET_REGION, region: REGION_REST_OF_LATAM });
            }
        } catch (e) {
            yield put({ type: types.SET_REGION, region: REGION_REST_OF_LATAM });
        }
    }
}

function* goBackToLoginAndShowMessage({ message }) {
    yield put(notificationActions.showNotification(message, "error", ["login"]));
    yield put({ type: globalTypes.BACK_TO_STEP_0 });
    yield put(replace("/"));
}

function* reset() {
    yield put(sessionActions.logout());
    yield put(replace("/"));
}

function* handleFinalizeLogin({ response }) {
    const activeRegion = yield select(selectors.getRegion);
    const { isAdministrator, generalConditions, userId } = response.data.data;
    let { _accessToken: accessToken } = response.data.data;
    const isAssistantLogin = yield select(assistantSelectors.isAssistantLogin);

    if (
        configUtils.get("core.sessionHandler.componentFQN") !==
        "com.technisys.omnichannel.core.session.DbSessionHandler"
    ) {
        accessToken = yield select(sessionSelectors.getAccessToken);
    }

    if (generalConditions) {
        const {
            generalConditionsShowExpiration,
            generalConditionsText,
            generalConditionsExpirationDate,
        } = response.data.data;
        const termsAndConditions = {
            // just joining the long named variables into a single object
            showExpiration: generalConditionsShowExpiration,
            generalConditions,
            body: generalConditionsText,
            expirationDate: generalConditionsExpirationDate,
        };

        yield put({ type: types.LOGIN_STEP_3_SUCCESS, termsAndConditions });
        if (activeRegion === REGION_USA) {
            const firstName = yield select(selectors.getUserFirstName);
            const email = yield select(selectors.getUsername);
            yield put({
                type: enrollmentTypes.SET_INVITATION,
                invitation: {
                    email,
                    firstName,
                    lastName: "",
                },
            });
            yield put(replace("/enrollment/Step3Part4"));
        } else {
            const uxLoginImprovements = configUtils.getBoolean("frontend.UXImprovements.Login.functionalities");
            yield put(replace(getLoginStep4Path(uxLoginImprovements)));
        }
    } else if (isAssistantLogin) {
        if (
            configUtils.get("core.sessionHandler.componentFQN") ===
            "com.technisys.omnichannel.core.session.DbSessionHandler"
        ) {
            if (accessToken.includes("|")) {
                const [splittedAccessToken] = accessToken.split("|");
                accessToken = splittedAccessToken;

                yield put(assistantActions.setMessengerPSID(splittedAccessToken[1]));
            }
        }
        yield put(assistantActions.setAccessToken(accessToken));
        yield put(replace("/loginStep5"));
    } else {
        const loginData = yield call(processLoginSuccess, response);
        const { environments, lang } = response.data.data;
        const firstName = yield select(selectors.getUserFirstName);

        configUtils.setRecaptchaLang(lang);
        yield put(i18nActions.setLang(lang));
        if (
            configUtils.get("core.sessionHandler.componentFQN") ===
            "com.technisys.omnichannel.core.session.DbSessionHandler"
        ) {
            yield call(session.setAuthToken, accessToken);
        }
        yield put({
            type: types.LOGIN_SUCCESS,
            environment: loginData.environment,
            user: loginData.user,
            environments,
            isAdministrator,
        });
        yield put({
            type: types.KEEP_USER_FIRST_NAME,
            userFirstName: firstName,
        });

        if (window.isMobileApp()) {
            yield fork(handlePostLoginMobileTasks, userId);
        }
        const uxDashboardImprovements = isUxDashboardImprovementsEnabled();
        yield put(replace(getPostLoginHomePath(uxDashboardImprovements)));
    }
}

function* handlePostLoginMobileTasks(userId) {
    yield put({
        type: tokenTypes.TOKEN_STATUS_PASSWORD_REQUEST,
        userId,
    });
}

export function* handleLoginStep1Request({
    username,
    rememberDocument,
    document,
    documentType,
    recaptchaResponse,
    formikBag,
    skipStepTransitionAnimation,
}) {
    const response = yield call(session.loginStep1, username, document, documentType, recaptchaResponse);
    const userpassNotifyDays = configUtils.getInteger("login.userpass.validity.notify.days", 10);
    const userpassNotificationShowed = yield select(selectors.getUserpassNotificationShowed);

    if (response.data.data.error) {
        yield put({ type: types.LOGIN_RESET_CAPTCHA, resetCaptcha: true });
        if (response.data.data.error === "login.step1.pin.expired") {
            const docValue =
                formikBag.props?.document ?? formikBag.values?.document?.number ?? formikBag.values?.document ?? "";
            if (yield select(isLoginV3PathSelector)) {
                formikBag.setValues({
                    ...formikBag.values,
                    document: { number: docValue, type: { value: "DNI", label: "DNI" } },
                    user: "",
                });
            } else {
                formikBag.setValues({
                    documentType: "DNI",
                    document: docValue,
                    username: "",
                });
            }
        }
        formikBag.setSubmitting(false);
        if (response.data.data.error === "login.step1.deleted.user") {
            yield put(
                notificationActions.showNotification(
                    i18n.get(response.data.data.error),
                    "error",
                    ["login"],
                    i18n.get(`${response.data.data.error}.title`),
                ),
            );
        } else if (response.data.data.error === "login.step1.pin.expired") {
            yield put(
                notificationActions.showNotification(
                    configUtils.get("login.Username.vencido.snackbar"),
                    "error",
                    ["login"],
                    i18n.get(`${response.data.data.error}.title`),
                    false,
                ),
            );
        } else if (
            response.data.data.error === "login.step1.blocked.disable.document" ||
            response.data.data.error === "login.step1.blocked.document"
        ) {
            yield put(
                notificationActions.showNotification(
                    response.data.data.message,
                    "error",
                    ["login"],
                    i18n.get(`${response.data.data.error}.title`),
                    false,
                ),
            );
        } else if (response.data.data.error === "login.ambientebloqueado.snackbar") {
            yield put(
                notificationActions.showNotification(response.data.data.message, "error", ["login"], null, false),
            );
            if (yield select(isLoginV3PathSelector)) {
                formikBag.setValues({
                    ...formikBag.values,
                    document: { number: "", type: { value: "DNI", label: "DNI" } },
                    user: "",
                });
            } else {
                formikBag.setValues({
                    documentType: "DNI",
                    document: "",
                    username: "",
                });
            }
        } else if (
            response.data.data.error === "login.step1.wrong.credentials" ||
            response.data.data.error === "login.step1.wrong.document" ||
            response.data.data.error === "login.step1.wrong.user"
        ) {
            // Anti user-enumeration: avanzamos siempre al step 2 (password) aunque las credenciales
            // del step 1 sean inválidas. Así, a nivel UX y a nivel red, el flujo es indistinguible
            // entre "documento inexistente" y "documento existente con usuario/PIN inválido".
            // El mensaje de error genérico se mostrará recién al intentar autenticar el password.
            if (yield select(isLoginV3PathSelector)) {
                yield put(loginActions.setFieldErrors({}));
            } else {
                formikBag.setErrors({});
            }

            // Pedimos un security seal aleatorio para que el step 2 se renderice con una imagen
            // (de lo contrario, la ausencia de seal podría delatar el caso de error).
            let randomSeal = "";
            let randomSealAlt = "";
            try {
                const sealResp = yield call(session.getRandomSecuritySeal);
                if (sealResp && sealResp.type !== "W") {
                    randomSeal = sealResp.data.data._securitySeal || "";
                    randomSealAlt = sealResp.data.data._securitySealAlt || "";
                }
            } catch (e) {
                // ignoramos: el step 2 puede renderizarse sin seal
            }

            yield put({
                type: types.LOGIN_STEP_1_SUCCESS,
                exchangeToken: NOTOKENLOGIN,
                securitySeal: randomSeal,
                securitySealAlt: randomSealAlt,
                userFirstName: "",
                username: documentType + document,
                lastLogin: null,
                lang: undefined,
                userMaskedEmail: "",
                userMaskedPhone: "",
                daysToExpireCredential: {},
                step1CredentialsInvalid: true,
            });

            if (response.data.data.showCaptcha) {
                yield put({ type: types.LOGIN_FAILURE_REQUIRE_CAPTCHA });
            }

            formikBag.setSubmitting(false);
            yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });

            const v3Path = yield select(isLoginV3PathSelector);
            if (v3Path) {
                yield put(replace("/v3/login/step2"));
            } else {
                yield put(replace("/loginStep2"));
            }
            return;
        } else if (response.data.data.error === "login.step1.captcha.required") {
            yield put({ type: types.LOGIN_FAILURE_REQUIRE_CAPTCHA });
            formikBag.setErrors({ captcha: i18n.get("login.step1.captcha.required") });
            formikBag.setTouched({ captcha: true });
            if (yield select(isLoginV3PathSelector)) {
                formikBag.setValues({
                    ...formikBag.values,
                    document: { number: document, type: { value: documentType, label: documentType } },
                    user: username,
                    captcha: "",
                });
            } else {
                formikBag.setValues({
                    documentType,
                    document,
                    username,
                    captcha: "",
                });
            }
        } else {
            yield put(notificationActions.showNotification(i18n.get(response.data.data.error), "error", ["login"]));
        }
        yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
    } else {
        const {
            _exchangeToken,
            _securitySeal,
            _securitySealAlt,
            _userFirstName,
            lang,
            lastLogin,
            _userMaskedEmail,
            _userMaskedPhoneNumber,
            daysToExpireCredential,
        } = response.data.data;
        const isV3AutoLogin = !!skipStepTransitionAnimation;
        const shouldShowUserpassWarning =
            parseInt(daysToExpireCredential.userpassDaysToExpire) <= userpassNotifyDays && !userpassNotificationShowed;
        if (shouldShowUserpassWarning && !isV3AutoLogin) {
            formikBag.setSubmitting(false);

            yield put(
                notificationActions.showNotification(
                    daysToExpireCredential.userpassMessage,
                    "warning",
                    ["login"],
                    i18n.get("enrollment.stepIRS.warning.title"),
                ),
            );
            yield put({
                type: types.USERPASS_NOTIFICATION_SHOWED,
            });
        } else {
            yield put({
                type: types.LOGIN_STEP_1_SUCCESS,
                exchangeToken: _exchangeToken,
                securitySeal: _securitySeal,
                securitySealAlt: _securitySealAlt,
                userFirstName: _userFirstName,
                username: documentType + document,
                lastLogin,
                lang,
                userMaskedEmail: _userMaskedEmail,
                userMaskedPhone: _userMaskedPhoneNumber,
                daysToExpireCredential,
            });
            yield put(tokenActions.setFirstName(_userFirstName));
            if (rememberDocument) {
                yield put(
                    loginActions.setRememberedUser({
                        document,
                        documentType,
                        username,
                        userpassUsername: username,
                        usernameFromRememberedUser: true,
                        userFirstName: _userFirstName,
                    }),
                );
            }
            const pathname = yield select((s) => s.router?.location?.pathname || "");
            if (pathname.includes("/v3")) {
                yield put(
                    replace({
                        pathname: "/v3/login/step2",
                        state: {
                            skipStepTransitionAnimation: !!skipStepTransitionAnimation,
                            showUserpassWarning: shouldShowUserpassWarning && isV3AutoLogin,
                            userpassWarningMessage: daysToExpireCredential.userpassMessage,
                        },
                    }),
                );
            } else {
                yield put(replace("/loginStep2"));
            }
        }
    }
}

function* showInvalidCredentialsNotification() {
    const isV3LoginPath = yield select(isLoginV3PathSelector);
    const rememberedUser = yield select(selectors.getRememberedUser);
    const step1CredentialsInvalid = yield select(selectors.getStep1CredentialsInvalid);
    const hasRememberedUser = hasRememberedCredentials(rememberedUser);
    const shouldReturnToStep1 =
        isV3LoginPath && (!hasRememberedUser || step1CredentialsInvalid);

    if (shouldReturnToStep1) {
        if (step1CredentialsInvalid && hasRememberedUser) {
            yield put(loginActions.removeRememberedUser());
        }
        yield put(loginActions.initLoginFlow());
        yield put(replace("/v3/login"));
    }

    yield put(
        notificationActions.showNotification(i18n.get("login.step1.wrong.credentials"), "error", [
            shouldReturnToStep1 ? "login" : "loginStep2",
        ]),
    );
}

const focusLoginStep2Password = () => {
    if (typeof document === "undefined") {
        return;
    }

    const passwordInput = document.getElementById("password");
    if (!passwordInput) {
        return;
    }

    const focus = () => passwordInput.focus();
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
        window.requestAnimationFrame(focus);
    } else {
        focus();
    }
};

const clearLoginStep2Values = (formikBag) => {
    formikBag.setValues({ password: "", captcha: "" }, false);
    focusLoginStep2Password();
};

function* finishLoginRequest(formikBag) {
    formikBag.setSubmitting(false);
    yield put(loginActions.resetLoading());
}

export function* handleLoginStep2Request({ password, recaptchaResponse, formikBag }) {
    const exchangeToken = yield select(selectors.getExchangeToken);

    if (exchangeToken === NOTOKENLOGIN) {
        try {
            clearLoginStep2Values(formikBag);
            yield put(loginActions.setResetCaptcha(true));
            yield* showInvalidCredentialsNotification();
            yield put({
                type: formTypes.SEND_FORM_DATA_FAILURE,
                idForm: "loginStep2",
                code: "COR020W",
                errors: null,
            });
        } finally {
            yield* finishLoginRequest(formikBag);
        }
        return;
    }
    const response = yield call(session.loginStep2, exchangeToken, password, recaptchaResponse);
    if (response.type === "W") {
        try {
            formikBag.setErrors(adjustIdFieldErrors(response.data.data || {}));

            if (response.data.code === "COR020W" || response.data.code === "API019W") {
                clearLoginStep2Values(formikBag);
                yield put(loginActions.setResetCaptcha(true));
                yield* showInvalidCredentialsNotification();
            } else if (response.data.code === "API020W" || response.data.code === "COR050W") {
                clearLoginStep2Values(formikBag);
                yield put(loginActions.setResetCaptcha(true));
                yield put(
                    notificationActions.showNotification(i18n.get("login.step1.wrong.credentials"), "error", [
                        "loginStep2",
                    ]),
                );
            } else {
                // exchangeToken expired, restart flow
                yield put(notificationActions.showNotification(response.data.message, "error", ["login"]));
                yield put({ type: globalTypes.BACK_TO_STEP_0 });
                yield put(replace("/"));
            }

            yield put({
                type: formTypes.SEND_FORM_DATA_FAILURE,
                idForm: "loginStep2",
                code: response.data.code,
                errors: response.data.data,
            });
        } finally {
            yield* finishLoginRequest(formikBag);
        }
    } else {
        const { environments, selectEnvironment } = response.data.data;

        yield put({ type: types.LOGIN_STEP_2_SUCCESS, environments });
        if (selectEnvironment && Object.keys(environments).length > 1) {
            const pathname = yield select((s) => s.router?.location?.pathname || "");
            yield put(replace(pathname.includes("/v3") ? "/v3/login/step3" : "/loginStep3"));
        } else {
            yield put({
                type: types.LOGIN_STEP_3_REQUEST,
                formikBag,
            });
        }
    }
}

// Códigos que devuelve session.login.legacy.step2 cuando el exchange token dejó de ser válido
// (vencido, inexistente, o emitido para otro usuario/IP/user agent). En estos casos el password
// no es el motivo del rechazo, por lo que no debe mostrarse "la contraseña es incorrecta".
const EXCHANGE_TOKEN_RETURN_CODES = ["API006E", "API007E", "API008E", "API009E", "API532E"];

// Reejecuta el step 1 con las credenciales recordadas para obtener un exchange token nuevo,
// de modo que el usuario pueda reintentar el password sin volver al inicio del login.
export function* refreshExchangeToken() {
    const rememberedUser = yield select(selectors.getRememberedUser);
    const pin = rememberedUser?.userpassUsername || rememberedUser?.username;
    const document = rememberedUser?.document;
    const documentType = rememberedUser?.documentType;

    if (!pin || !document || !documentType) {
        return false;
    }

    try {
        const response = yield call(session.loginStep1, pin, document, documentType, "");
        const data = response?.data?.data;

        if (response?.type === "W" || !data || data.error || !data._exchangeToken) {
            return false;
        }

        yield put({
            type: types.LOGIN_STEP_1_SUCCESS,
            exchangeToken: data._exchangeToken,
            securitySeal: data._securitySeal,
            securitySealAlt: data._securitySealAlt,
            userFirstName: data._userFirstName,
            username: documentType + document,
            lastLogin: data.lastLogin,
            lang: data.lang,
            userMaskedEmail: data._userMaskedEmail,
            userMaskedPhone: data._userMaskedPhoneNumber,
            daysToExpireCredential: data.daysToExpireCredential,
        });

        return true;
    } catch (error) {
        return false;
    }
}

export function* handleInvalidExchangeToken({ code, message, formikBag }) {
    const isV3LoginPath = yield select(isLoginV3PathSelector);
    const tokenRefreshed = yield call(refreshExchangeToken);

    if (tokenRefreshed) {
        if (isV3LoginPath) {
            yield put(loginActions.setFieldErrors({ password: message }));
        } else {
            formikBag.setErrors({ password: message });
        }
    } else {
        yield put(notificationActions.showNotification(message, "error", ["login"]));
        yield put(replace(getUnauthenticatedLoginPath(isV3LoginPath)));
    }

    yield put(loginActions.resetLoading());
    yield put({
        type: formTypes.SEND_FORM_DATA_FAILURE,
        code,
    });
}

export function* handleOauthRequest({ password, recaptchaResponse, formikBag, showCaptcha }) {
    const exchangeToken = yield select(selectors.getExchangeToken);

    // Anti user-enumeration: si el step 1 falló con credenciales inválidas, ya nos navegaron
    // al step 2 con exchangeToken = NOTOKENLOGIN. Ahora que el usuario ingresó el password,
    // mostramos el mensaje genérico sin diferenciar el motivo del fallo del step 1.
    if (exchangeToken === NOTOKENLOGIN) {
        try {
            clearLoginStep2Values(formikBag);
            yield put(loginActions.setResetCaptcha(true));
            yield* showInvalidCredentialsNotification();
            yield put({
                type: formTypes.SEND_FORM_DATA_FAILURE,
                idForm: "loginStep2",
                code: "COR020W",
                errors: null,
            });
        } finally {
            yield* finishLoginRequest(formikBag);
        }
        return;
    }

    const username = yield select(selectors.getUsername);
    const isFromMessenger = yield select(assistantSelectors.isFromMessenger);
    // eslint-disable-next-line camelcase
    const { redirect_uri } = yield select(assistantSelectors.getParams);
    const isFromWhatsapp = yield select(assistantSelectors.isFromWhatsapp);

    let response;
    if (isFromMessenger || isFromWhatsapp) {
        const thirdPartyToken = yield select(assistantSelectors.getThirdPartyToken);
        response = yield call(
            session.thirdPartyOauth,
            username,
            password,
            thirdPartyToken,
            redirect_uri,
            exchangeToken,
            recaptchaResponse,
        );
        if (response.data) {
            response.data.access_token = thirdPartyToken;
        } else {
            response.data = {
                access_token: thirdPartyToken,
            };
        }
    } else {
        if (showCaptcha) {
            response = yield call(session.validateCaptcha, recaptchaResponse);
            if (response.type === "W") {
                yield put({ type: types.LOGIN_FAILURE_REQUIRE_CAPTCHA });
                yield put({ type: types.LOGIN_RESET_CAPTCHA, resetCaptcha: true });
                formikBag.setErrors({ captcha: i18n.get("login.step1.captcha.required") });
                formikBag.setValues({ captcha: "" }, false);
                yield* finishLoginRequest(formikBag);
                yield put({
                    type: formTypes.SEND_FORM_DATA_FAILURE,
                    code: response.data.error_description,
                });
                return;
            }
        }
        const useOIDC = window.OAUTH_OIDC_URL && window.OAUTH_OIDC_URL !== "";
        if (useOIDC) {
            // OIDC: call step2 directly to get return codes, then Keycloak with exchange token
            try {
                const step2Response = yield call(session.oauthStep2Direct, username, `${password}#${exchangeToken}#`);
                if (step2Response.type === "I") {
                    // step2 OK — call Keycloak with just the exchange token
                    const updatedExchangeToken = step2Response.data.data._exchangeToken;
                    response = yield call(session.oauthKeycloak, username, updatedExchangeToken);
                } else {
                    // step2 warning (API019W, etc.) — wrap for existing error handling
                    response = { type: "W", data: { error_description: step2Response.data.code } };
                }
            } catch (step2Error) {
                // step2 error codes ending in E (API022E, COR999E) throw from api.js interceptor
                const code = step2Error.data ? step2Error.data.code : "COR999E";
                response = { type: "W", data: { error_description: code } };
            }
        } else {
            // Legacy: existing flow
            response = yield call(session.oauth, username, password, exchangeToken);
        }
    }

    if (response.type === "W") {
        formikBag.setErrors(adjustIdFieldErrors({}));
        formikBag.setSubmitting(false);
        formikBag.setValues({
            password,
            captcha: "",
        });
        yield put({ type: types.LOGIN_RESET_CAPTCHA, resetCaptcha: true });
        const errorMsg = i18n.get(`returnCode.${response.data.error_description}`, response.data.error_description);
        if (response.data.error_description === "COR020W" || response.data.error_description === "API019W") {
            clearLoginStep2Values(formikBag);
            yield put(loginActions.setResetCaptcha(true));
            yield* showInvalidCredentialsNotification();
        } else if (
            response.data.error_description === "API020W" ||
            response.data.error_description === "COR050W" ||
            response.data.error_description === "API022E" ||
            response.data.error_description === "API517W"
        ) {
            if (
                response.data.error_description === "API020W" ||
                response.data.error_description === "COR050W" ||
                response.data.error_description === "API022E" ||
                response.data.error_description === "API517W"
            ) {
                if (response.data.error_description === "API020W") {
                    // Captcha required || captcha invalid
                    // Anti user-enumeration: mostramos el mismo snackbar genérico que en COR020W/API019W
                    // en lugar de marcar "contraseña incorrecta" como error de campo.
                    yield put({ type: types.LOGIN_FAILURE_REQUIRE_CAPTCHA });
                    yield put({ type: types.LOGIN_RESET_CAPTCHA, resetCaptcha: true });
                    clearLoginStep2Values(formikBag);
                    yield put(loginActions.setResetCaptcha(true));
                    yield put(
                        notificationActions.showNotification(i18n.get("login.step1.wrong.credentials"), "error", [
                            "loginStep2",
                        ]),
                    );
                    yield put(loginActions.resetLoading());
                }
                // Se bloqueo o expiro la password
                else if (
                    response.data.error_description === "API022E" ||
                    response.data.error_description === "COR050W" ||
                    response.data.error_description === "API517W"
                ) {
                    // se bloqueo la password por cantidad de intentos
                    if (
                        response.data.error_description === "API022E" ||
                        response.data.error_description === "COR050W"
                    ) {
                        yield put(
                            notificationActions.showNotification(
                                configUtils.get("login.credencialesbloqueadas.snackbar"),
                                "error",
                                ["loginStep2"],
                                i18n.get("login.step2.password.blocked.tittle"),
                                false,
                            ),
                        );
                    } else {
                        // Expiro la password
                        yield put(
                            notificationActions.showNotification(
                                configUtils.get("login.Password.vencida.snackbar"),
                                "error",
                                ["loginStep2"],
                                i18n.get("login.step2.password.expired.title"),
                                false,
                            ),
                        );
                    }
                } else {
                    yield put(notificationActions.showNotification(errorMsg, "error", ["loginStep2"]));
                }
            } else {
                clearLoginStep2Values(formikBag);
                yield put(loginActions.setResetCaptcha(true));
                yield put(
                    notificationActions.showNotification(i18n.get("login.step1.wrong.credentials"), "error", [
                        "loginStep2",
                    ]),
                );
            }

            yield put(loginActions.resetLoading());
            yield put({
                type: formTypes.SEND_FORM_DATA_FAILURE,
                code: response.data.error_description,
            });
        } else if (EXCHANGE_TOKEN_RETURN_CODES.includes(response.data.error_description)) {
            // El login expiró (o el exchange token dejó de ser válido): mostramos el mensaje del
            // returnCode debajo del campo password en vez de "la contraseña es incorrecta".
            yield call(handleInvalidExchangeToken, {
                code: response.data.error_description,
                message: errorMsg,
                formikBag,
            });
        } else if (response.data.error === "invalid_grant") {
            // Anti user-enumeration: mismo snackbar gen\u00e9rico (no field error espec\u00edfico).
            clearLoginStep2Values(formikBag);
            yield put(loginActions.setResetCaptcha(true));
            yield* showInvalidCredentialsNotification();
            yield put(loginActions.resetLoading());
            yield put({
                type: formTypes.SEND_FORM_DATA_FAILURE,
                code: response.data.error_description,
            });
        } else {
            // exchangeToken expired, restart flow (o error OAuth sin código CMF, ej. invalid_grant)
            yield put({ type: types.BACK_TO_STEP_0 });
            const isV3LoginPath = yield select(isLoginV3PathSelector);
            const rememberedUser = yield select(selectors.getRememberedUser);
            const stayOnLoginStep2 = isV3LoginPath && hasRememberedCredentials(rememberedUser);
            if (stayOnLoginStep2) {
                const invalidPasswordMsg = i18n.get("login.step2.wrong.password");
                yield put(loginActions.setFieldErrors({ password: invalidPasswordMsg }));
                yield put(loginActions.resetLoading());
            }
            if (!isV3LoginPath && !hasRememberedCredentials(rememberedUser)) {
                yield put(notificationActions.showNotification(errorMsg, "error", ["login"]));
                yield put(replace("/"));
            }
        }
        yield* finishLoginRequest(formikBag);
    } else {
        // Ignored because it's the OAUTH standard
        // eslint-disable-next-line camelcase
        const { access_token, refresh_token } = response.data;

        if (!isFromMessenger && !isFromWhatsapp) {
            session.setAuthToken(access_token);
            yield put(sessionActions.setTokens(access_token, refresh_token));
        } else {
            yield put(assistantActions.setAccessToken(access_token));
            yield put(replace("/loginStep5"));
            return;
        }
        const environmentsResponse = yield call(session.listEnvironments);

        const { environments, selectEnvironment } = environmentsResponse.data.data;
        yield put({ type: types.LOGIN_OAUTH_SUCCESS, environments });
        if (selectEnvironment && Object.keys(environments).length > 1) {
            const pathname = yield select((s) => s.router?.location?.pathname || "");
            yield put(replace(pathname.includes("/v3") ? "/v3/login/step3" : "/loginStep3"));
        } else if (!environments && !selectEnvironment) {
            yield put({ type: globalTypes.BACK_TO_STEP_0 }); // por lo que borro lo que tengo de sesion y voy al step0
            yield put(
                notificationActions.showNotification(i18n.get("login.step2.environments.unavailable"), "error", [
                    "login",
                ]),
            );
            yield put(replace("/"));
        } else {
            yield put({
                type: types.LOGIN_STEP_3_REQUEST,
                formikBag,
            });
        }
    }
}

function* handleLoginStep3Request({ idEnvironment, rememberEnvironment, formikBag }) {
    const exchangeToken = yield select(selectors.getExchangeToken);
    const isFromMessenger = yield select(assistantSelectors.isFromMessenger);
    const isFromGoogle = yield select(assistantSelectors.isFromGoogle);
    const isFromAmazon = yield select(assistantSelectors.isFromAmazon);
    const isFromWhatsapp = yield select(assistantSelectors.isFromWhatsapp);
    const clientNumber = yield select(assistantSelectors.getClientNumber);
    const accountLinkingToken = yield select(assistantSelectors.getAccountLinkingToken);

    const position = yield call(currentLocation, { lat: 0.0, lng: 0.0 });
    const location = {
        latitude: position.lat,
        longitude: position.lng,
    };

    let extraInfo = {};
    let idDevice = null;
    let registrationId = null;

    if (window.app) {
        extraInfo = window.device;
        extraInfo.isTablet = false;
        extraInfo.isIOS = deviceUtils.getMobileOS(deviceUtils.getDisplay()) === deviceUtils.IOS_DEVICE;
        extraInfo.isAndroid = deviceUtils.getMobileOS(deviceUtils.getDisplay()) === deviceUtils.ANDROID_DEVICE;
        extraInfo.uuid = window.app.getDeviceUUID();
        idDevice = window.app.getDeviceUUID();
        const response = yield call(pushNotificationsUtils.getTokenPush);
        const registrationIdPlugin = response?.registrationId;
        registrationId = registrationIdPlugin;
    } else {
        idDevice = yield deviceUtils.getDeviceFingerprint();
        extraInfo = yield deviceUtils.getExtraInfo();
    }

    yield call(session.registerUserDevice, exchangeToken, idDevice, registrationId, JSON.stringify(extraInfo));

    const response = yield call(
        session.loginStep3,
        exchangeToken,
        idEnvironment,
        rememberEnvironment,
        location,
        idDevice,
        {
            isFromMessenger,
            isFromGoogle,
            isFromAmazon,
            isFromWhatsapp,
            accountLinkingToken,
            clientNumber,
        },
    );

    if (response.type === "W") {
        const { code, data } = response.data;
        if (code === "COR020W" && data.environment) {
            // mark the environment as disabled and redirecting the user to the step 3 again
            const environments = yield select(selectors.getEnvironments);
            if (environments[idEnvironment]) {
                environments[idEnvironment].allowedToAccess = false;
                yield put({ type: types.MARK_ENVIRONMENTS_DISABLED, environments });
            }
            const pathname = yield select((s) => s.router?.location?.pathname || "");
            yield put(replace(pathname.includes("/v3") ? "/v3/login/step3" : "/loginStep3"));
        } else {
            // en este paso lo unico que podria suceder es que se venciera el exchange token
            if (formikBag) {
                formikBag.setErrors(adjustIdFieldErrors(response.data.data));
                formikBag.setSubmitting(false);
            }
            yield put(notificationActions.showNotification(response.data.message, "error", ["login"]));
            yield put({ type: globalTypes.BACK_TO_STEP_0 }); // por lo que borro lo que tengo de sesion y voy al step0
            yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
            yield put(replace("/"));
        }
    } else {
        yield put({ type: types.FINALIZE_LOGIN, response });
    }
}

function* handleLoginStep4Request({ acceptConditions, formikBag }) {
    // const activeRegion = yield select(selectors.getRegion);
    const exchangeToken = yield select(selectors.getExchangeToken);
    const idEnvironmentToAcceptConditions = yield select(selectors.getIdEnvironmentToAcceptConditions);
    const response = yield call(session.loginStep4, exchangeToken, acceptConditions, idEnvironmentToAcceptConditions);

    if (response.type === "W") {
        if (formikBag) {
            formikBag.setErrors(adjustIdFieldErrors(response.data.data));
        }
        if (formikBag) {
            formikBag.setSubmitting(false);
        }
        yield put(notificationActions.showNotification(response.data.message, "error", ["loginStep4"]));
        yield put({ type: types.LOGIN_FAILURE, errors: response.data.data });
    } else {
        const loginData = yield call(processLoginSuccess, response);
        const { data } = response.data;

        configUtils.setRecaptchaLang(data.lang);
        yield put(i18nActions.setLang(data.lang));

        if (
            configUtils.get("core.sessionHandler.componentFQN") ===
            "com.technisys.omnichannel.core.session.DbSessionHandler"
        ) {
            // eslint-disable-next-line no-underscore-dangle
            yield call(session.setAuthToken, data._accessToken);
        }
        yield put({
            type: types.LOGIN_SUCCESS,
            environment: loginData.environment,
            user: loginData.user,
            environments: data.environments,
            isAdministrator: data.isAdministrator,
        });

        // const { pepCompleted, irsCompleted } = loginData.user;

        yield put(statusActions.resetComeFromLogin());
        const uxDashboardImprovements = isUxDashboardImprovementsEnabled();
        yield put(replace(getPostLoginHomePath(uxDashboardImprovements)));
    }
}

function processLoginSuccess(response) {
    const {
        _accessToken,
        _securitySeal,
        username,
        userFullName,
        userId,
        previousLoginInfo,
        defaultAvatarId,
        email,
        clients,
        signedTAndCDate,
        irsDate,
        pepCompleted,
        irsCompleted,
        ssnid,
        liberator,
        signatureLevel,
        ...data
    } = response.data.data;
    const user = {
        defaultAvatarId,
        previousLoginInfo,
        userFullName,
        userId,
        email,
        accessToken: _accessToken,
        securitySeal: _securitySeal,
        signedTAndCDate,
        irsDate,
        pepCompleted,
        irsCompleted,
        ssnid,
        liberator,
        signatureLevel,
        idDefaultEnvironment:
            data.idDefaultEnvironment && data.idDefaultEnvironment > 0 ? data.idDefaultEnvironment : null,
    };

    let forms = null;
    if (data.forms) {
        forms = {};

        for (let i = 0; i < data.forms.length; i++) {
            let category = forms[data.forms[i].category];
            if (!category) {
                category = [];
                forms[data.forms[i].category] = category;
            }
            category.push(data.forms[i]);
        }
    }

    const environment = {
        permissions: data.permissions,
        forms,
        name: data.activeEnvironmentName,
        type: data.activeEnvironmentType,
        id: data.activeIdEnvironment,
        administrationScheme: data.administrationScheme,
        clients,
    };

    return { user, environment };
}

function* handleFingerprintLoginPre() {
    const availability = yield call(fingerprintUtils.isAvailable);
    const hardwareAvailability = availability && availability.isAvailable && availability.isHardwareDetected;

    if (hardwareAvailability) {
        let fingerprintAuthToken = null;
        let fingerprintAuthTokenExists = true;

        try {
            fingerprintAuthToken = yield call(secureStorageUtils.get, "fingerprintAuthToken");
        } catch (error) {
            fingerprintAuthTokenExists = false;
        }

        if (fingerprintAuthTokenExists) {
            try {
                const result = yield call(fingerprintUtils.verifyAio);
                if (result && result.withFingerprint) {
                    // iOS WKWebView: al cerrar el prompt nativo de biometría el compositor
                    // congela la capa de scroll. Se hace un nudge inmediato del documentElement
                    // para despertar el compositor antes de que React renderice nuevas capas
                    // (spinner, animaciones del login v3 con @ui-kit) que agravan el freeze.
                    if (window.isMobileApp && window.isMobileApp()) {
                        try {
                            const de = document.documentElement;
                            de.scrollTop = (de.scrollTop || 0) + 1;
                            de.scrollTop = (de.scrollTop || 0) - 1;
                        } catch (scrollFixError) {
                            // ignorar
                        }
                    }
                    yield call(delay, 150);
                    yield put({ type: types.FINGERPRINT_SUBMIT });

                    const encryptedUsername = yield call(secureStorageUtils.get, "fingerprintUsername");
                    const username = decryptCredential(encryptedUsername);
                    const idDevice = yield call(secureStorageUtils.get, "fingerprintIdDevice");

                    const useOIDC = window.OAUTH_OIDC_URL && window.OAUTH_OIDC_URL !== "";
                    let responseFingerprintLogin;
                    if (useOIDC) {
                        // OIDC: call step2 with fingerprint#token#deviceId#model, get exchange token, then Keycloak
                        const model = deviceUtils.getDeviceModel();
                        const fpPassword = `fingerprint#${fingerprintAuthToken}#${idDevice}#${model}`;
                        const step2Resp = yield call(session.oauthStep2Direct, username, fpPassword);
                        if (step2Resp.type === "I" && step2Resp.data.data._exchangeToken) {
                            responseFingerprintLogin = yield call(
                                session.oauthKeycloak,
                                username,
                                step2Resp.data.data._exchangeToken,
                            );
                        } else {
                            // Wrap step2 error in OAuth-like format
                            responseFingerprintLogin = { type: "W", data: { error_description: step2Resp.data.code } };
                        }
                    } else {
                        responseFingerprintLogin = yield call(
                            session.fingerprintOauth,
                            username,
                            fingerprintAuthToken,
                            idDevice,
                        );
                    }

                    if (responseFingerprintLogin.type === "W") {
                        if (responseFingerprintLogin.data.error_description === "API045W") {
                            yield put(loginActions.removeRememberedUser());
                            yield put(loginActions.removeBiometricLoginShow());
                            yield put({ type: fingerprintTypes.CLEAN_UP });

                            yield put(
                                notificationActions.showNotification(
                                    i18n.get("biometrics.error.invalid.config"),
                                    "error",
                                    ["login"],
                                ),
                            );
                            yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                        } else if (responseFingerprintLogin.data.error_description === "API022E") {
                            yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                            yield put(
                                push({
                                    pathname: "/error",
                                    code: responseFingerprintLogin.data.error_description,
                                    isBiometricLogin: true,
                                }),
                            );
                        } else {
                            yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                            yield put(
                                push({
                                    pathname: "/serverError",
                                    isBiometricLogin: true,
                                }),
                            );
                        }
                    } else {
                        // Ignored because it's the OAUTH standard
                        // eslint-disable-next-line camelcase
                        const { access_token, refresh_token } = responseFingerprintLogin.data;

                        session.setAuthToken(access_token);
                        yield put(sessionActions.setTokens(access_token, refresh_token));

                        const environmentsResponse = yield call(session.listEnvironments);

                        const { environments, selectEnvironment } = environmentsResponse.data.data;

                        yield put({ type: types.LOGIN_OAUTH_SUCCESS, environments });

                        if (selectEnvironment && Object.keys(environments).length > 1) {
                            const pathname = yield select((s) => s.router?.location?.pathname || "");
                            yield put(replace(pathname.includes("/v3") ? "/v3/login/step3" : "/loginStep3"));
                        } else if (!environments && !selectEnvironment) {
                            // por lo que borro lo que tengo de sesion y voy al step0
                            yield put({ type: globalTypes.BACK_TO_STEP_0 });
                            yield put(
                                notificationActions.showNotification(
                                    i18n.get("login.step2.environments.unavailable"),
                                    "error",
                                    ["login"],
                                ),
                            );
                            yield put(replace("/"));
                            yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                        } else {
                            yield put({
                                type: types.LOGIN_STEP_3_REQUEST,
                            });
                        }
                    }
                } else {
                    yield put(
                        notificationActions.showNotification(i18n.get("biometrics.error.login.general"), "error", [
                            "login",
                        ]),
                    );
                }
            } catch (error) {
                const errorCode = error.code;
                if (
                    errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_DISMISSED ||
                    errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_PIN_OR_PATTERN_DISMISSED
                ) {
                    yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                } else if (
                    errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT ||
                    errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_LOCKED_OUT_PERMANENT
                ) {
                    yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                    const message = configUtils.get("biometric.NumberAttemptsPassed.Snackbar");
                    yield put(notificationActions.showNotification(message, "error", ["login"]));
                } else if (errorCode === fingerprintUtils.fingerprintErrors.BIOMETRIC_NOT_ENROLLED) {
                    yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                    const message = configUtils.get("biometric.NumberAttemptsPassed.Snackbar");
                    yield put(notificationActions.showNotification(message, "error", ["login"]));
                } else {
                    // eslint-disable-next-line no-console
                    console.log("Fingerprint - The following error has ocurred: ", error);
                    if (error.status && error.status === 200) {
                        yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                        yield put(
                            push({
                                pathname: "/error",
                                code: error.data.code,
                                message: error.data.message,
                            }),
                        );
                    } else if (error.response && error.response.status && error.response.status === 401) {
                        yield put(
                            notificationActions.showNotification(
                                i18n.get("login.fingerprint.session.expired"),
                                "error",
                                ["login"],
                            ),
                        );
                        yield put({ type: fingerprintTypes.CLEAN_UP });
                        yield call(secureStorageUtils.remove, "fingerprintAuthToken");
                        yield put({ type: types.BACK_TO_STEP_0 });
                        yield put(replace("/"));
                    } else if (deviceUtils.getDevicePlatform() === "iOS" && error.code === -25293) {
                        yield put(loginActions.removeRememberedUser());
                        yield put(loginActions.removeBiometricLoginShow());

                        yield put({ type: fingerprintTypes.CLEAN_UP });

                        yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });

                        yield put(
                            notificationActions.showNotification(i18n.get("biometrics.error.invalid.config"), "error", [
                                "login",
                            ]),
                        );
                    } else if (error.response && error.response.status && error.response.status === 500) {
                        yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                        yield put(
                            push({
                                pathname: "/error",
                                code: error.response.status,
                                message: error.statusText,
                            }),
                        );
                    } else if (error.code !== -128) {
                        const message =
                            deviceUtils.getDevicePlatform() === "iOS" ? error?.localizedDescription : error?.message;
                        if (message) {
                            yield put(notificationActions.showNotification(message, "error", ["login"]));
                        }
                    } else {
                        yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });
                    }
                }
            }
        } else {
            yield put(
                notificationActions.showNotification(i18n.get("biometrics.error.invalid.token"), "error", ["login"]),
            );
            yield put(loginActions.fingerprintLoginFailure());
        }
    } else {
        yield put(loginActions.removeRememberedUser());
        yield put(loginActions.removeBiometricLoginShow());

        yield put({ type: fingerprintTypes.CLEAN_UP });

        yield put({ type: types.LOGIN_FINGERPRINT_FAILURE });

        const message = configUtils.get("biometric.noAvailable.general.error");

        yield put(notificationActions.showNotification(message, "error", ["login"]));
    }
}

function* handleDownloadTerms({ format }) {
    const { type, data } = yield call(session.exportTermsAndConditions, format);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_TERMS_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["settings"]));
    } else {
        const { content, fileName } = data.data;
        utilDownload.downloadPdf(fileName, content);
        yield put(notificationActions.showNotification(i18n.get("global.download.success"), "success", ["settings"]));
        yield put({ type: types.DOWNLOAD_TERMS_SUCCESS });
    }
}

function* handleDownloadTermsI18n({ format, i18nVar }) {
    const { type, data } = yield call(session.exportTermsAndConditionsI18nVar, format, i18nVar);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_TERMS_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["settings"]));
    } else {
        const { content, fileName } = data.data;
        utilDownload.downloadPdf(fileName, content);
        yield put({ type: types.DOWNLOAD_TERMS_SUCCESS });
    }
}
